"use client";

import type { ReactNode } from "react";
import { AiBrowsingTracker } from "@/components/ai/entry/ai-browsing-tracker";
import { useLayout } from "@/contexts/layout-context";
import { useView } from "@/contexts/view-context";
import { TabNavigationProvider } from "@/contexts/tab-navigation-context";
import { useIsMobile } from "@/hooks/useIsMobile";
import BabyTycheBanner from "../shared/baby-tyche-banner";
import MobileHeader from "../shared/MobileHeader";
import Footer from "./footer";
import Header from "./header";

interface MainLayoutProps {
	children: ReactNode;
	showMobileNav?: boolean;
	showHeaders?: boolean;
	showFooter?: boolean;
	mainClassName?: string;
	rootClassName?: string;
}

export default function MainLayout({
	children,
	showMobileNav = true,
	showHeaders = true,
	showFooter = true,
	mainClassName = "",
	rootClassName = "",
}: MainLayoutProps) {
	const { isMobile, isHydrated } = useIsMobile();
	const {
		mobileHeaderBackAction,
		mobileHeaderLeftAction,
		mobileHeaderRightActions,
	} = useLayout();
	const { viewMode } = useView();

	if (!isHydrated) {
		// hydration 完成前保留基础页面骨架，避免客户端路由时整页空白
		return (
			<TabNavigationProvider>
				<div className={`flex min-h-screen flex-col ${rootClassName}`}>
					<main className={`w-full flex-grow ${mainClassName}`}>
						{children}
					</main>
				</div>
			</TabNavigationProvider>
		);
	}

	const getModeBorder = () => {
		if (viewMode === "pro") return "border-t-2 border-cyan-500/50";
		if (viewMode === "focus") return "border-t-2 border-light-primary/50";
		return "";
	};

	return (
		<TabNavigationProvider>
			<div
				className={`flex min-h-screen flex-col ${getModeBorder()} ${rootClassName}`}
			>
				<AiBrowsingTracker />
				{showHeaders &&
					(isMobile ? (
						// 移动端视图
						<MobileHeader
							backAction={mobileHeaderBackAction}
							leftAction={mobileHeaderLeftAction}
							rightActions={mobileHeaderRightActions}
						/>
					) : (
						// 桌面端视图
						<>
							<BabyTycheBanner />
							<Header />
						</>
					))}

				<main
					className={`flex-grow w-full ${isMobile && showMobileNav ? "pb-16" : ""} ${mainClassName}`}
				>
					{children}
				</main>

				{showFooter ? <Footer /> : null}
			</div>
		</TabNavigationProvider>
	);
}
